import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { BarChart2, ShoppingBag, Zap } from "lucide-react";
import axios from 'axios';
import StatCard from "./StatCard";
import { useTheme } from "@emotion/react";
import UserInteractions from "./UserInteraction";
import Reports from "./Reports";
import DateRangeSelector from "../../components/DateRangeSelector";
import WeeklyTrendsChart from './WeeklyTrendsChart';
import HourlyPerformanceChart from './HourlyPerformanceChart';
import PerformanceMetricsChart from './PerformanceMetricsChart';
import MetricCards from './MetricCards';
import IntentsTable from './IntentsTable';
import DepartmentPieChart from './DepartmentPiechart';
import { DashboardSkeleton } from '../../components/SkeletonLoader';

const DashboardOverview = () => {
  const [data, setData] = useState({ overview: {}, callVolume: [], callTopics: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [hourlyData, setHourlyData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [avgResponseTime, setAvgResponseTime] = useState(0);
  const [humanHandoffRate, setHumanHandoffRate] = useState(0);
  const [firstContactResolution, setFirstContactResolution] = useState({ percent: 0, change: 0 });
  const [multiTurnSuccessRate, setMultiTurnSuccessRate] = useState(0);
  const [intentRecognitionAccuracy, setIntentRecognitionAccuracy] = useState({ accuracy: 0, change: 0 });
  const [escalationPrevention, setEscalationPrevention] = useState({ prevention: 0, change: 0 });
  const [peakHourPerformance, setPeakHourPerformance] = useState({ performance: 0, change: 0 });
  const [sentimentData, setSentimentData] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [intentTableData, setIntentTableData] = useState([]);
  const [departmentCallData, setDepartmentCallData] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    setOrganizationId(localStorage.getItem('userId'));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/statcards/${organizationId}`, {
          params: {
            startDate: dateRange.start,
            endDate: dateRange.end
          }
        });
        setData(prevData => ({ ...prevData, overview: response.data }));
      } catch (error) {
        console.error("Error fetching stat cards data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchHourlyData = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/hourlycallvolume/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        setHourlyData(
          response.data.map(row => ({
            hour: row.hour,
            hourLabel: row.hourLabel,
            hoursAgo: row.hoursAgo,
            volume: Number(row.volume)
          }))
        );
      } catch (err) {
        setHourlyData([]);
      }
    };
    fetchHourlyData();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchWeeklyData = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/weeklyinteraction/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        setWeeklyData(
          response.data.map(row => ({
            day: row.day,
            interactions: Number(row.interactions)
          }))
        );
      } catch (err) {
        setWeeklyData([]);
      }
    };
    fetchWeeklyData();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchPerformanceMetrics = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/performance-metrics/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        setPerformanceMetrics(response.data);
      } catch (err) {
        setPerformanceMetrics([]);
      }
    };
    fetchPerformanceMetrics();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchAvgResponseTime = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/avg-response-time/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setAvgResponseTime(response.data[0].avg_response_time_seconds || 0);
        }
      } catch (err) {
        setAvgResponseTime(0);
      }
    };
    fetchAvgResponseTime();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchHumanHandoffRate = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/human-handoff-rate/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setHumanHandoffRate(response.data[0].human_handoff_rate || 0);
        }
      } catch (err) {
        setHumanHandoffRate(0);
      }
    };
    fetchHumanHandoffRate();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchFirstContactResolution = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/first-contact-resolution/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setFirstContactResolution({
            percent: response.data[0].fcr_percent || 0,
            change: response.data[0].fcr_change || 0
          });
        }
      } catch (err) {
        setFirstContactResolution({ percent: 0, change: 0 });
      }
    };
    fetchFirstContactResolution();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchMultiTurnSuccessRate = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/multi-turn-success-rate/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setMultiTurnSuccessRate(response.data[0].multi_turn_success_rate || 0);
        }
      } catch (err) {
        setMultiTurnSuccessRate(0);
      }
    };
    fetchMultiTurnSuccessRate();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchIntentRecognitionAccuracy = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/intent-recognition-accuracy/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setIntentRecognitionAccuracy({
            accuracy: response.data[0].intent_recognition_accuracy || 0,
            change: response.data[0].change || 0
          });
        }
      } catch (err) {
        setIntentRecognitionAccuracy({ accuracy: 0, change: 0 });
      }
    };
    fetchIntentRecognitionAccuracy();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchEscalationPrevention = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/escalation-prevention/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setEscalationPrevention({
            prevention: response.data[0].escalation_prevention || 0,
            change: response.data[0].change || 0
          });
        }
      } catch (err) {
        setEscalationPrevention({ prevention: 0, change: 0 });
      }
    };
    fetchEscalationPrevention();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchPeakHourPerformance = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/peak-hour-performance/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setPeakHourPerformance({
            performance: response.data[0].peak_hour_performance || 0,
            change: response.data[0].change || 0
          });
        }
      } catch (err) {
        setPeakHourPerformance({ performance: 0, change: 0 });
      }
    };
    fetchPeakHourPerformance();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchSentimentAnalysis = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/sentiment-analysis/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          // Transform the data for the pie chart
          const transformedData = response.data.map(item => ({
            name: item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1), // Capitalize first letter
            value: Number(item.percentage) || 0,
            count: Number(item.count) || 0
          }));
          setSentimentData(transformedData);
        } else {
          setSentimentData([]);
        }
      } catch (err) {
        console.error('Error fetching sentiment analysis:', err);
        setSentimentData([]);
      }
    };
    fetchSentimentAnalysis();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchIntentTableData = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/intent-table/${organizationId}`
        );
        setIntentTableData(response.data.map(row => ({
          intent: row.Intent,
          interactions: Number(row.Interactions),
          avgTime: row.Avg_Time,
          level: row.Level,
          change: row.Change,
          success: row.Success
        })));
      } catch (err) {
        setIntentTableData([]);
        if (err.response) {
          console.error('Intent Table API Error:', err.response.data, err.response.status);
        } else {
          console.error('Intent Table Fetch Error:', err.message);
        }
      }
    };
    fetchIntentTableData();
  }, [organizationId, dateRange]);

  useEffect(() => {
    const fetchDepartmentWiseCallData = async () => {
      if (!organizationId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/department-wise-calls/${organizationId}`,
          { params: { startDate: dateRange.start, endDate: dateRange.end } }
        );
        if (response.data && response.data.length > 0) {
          setDepartmentCallData(response.data);
        } else {
          setDepartmentCallData([]);
        }
      } catch (err) {
        console.error('Error fetching department-wise call data:', err);
        setDepartmentCallData([]);
      }
    };
    fetchDepartmentWiseCallData();
  }, [organizationId, dateRange]);

  const handleDateRangeChange = (newDateRange) => {
    if (newDateRange.start && newDateRange.end) {
      setDateRange(newDateRange);
      fetchData(newDateRange);
    } else {
      console.error('Invalid date range received:', newDateRange);
    }
  };

  const fetchData = async (dateRange) => {
    if (!organizationId) {
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/statcards/${organizationId}`, {
        params: {
          startDate: dateRange?.start,
          endDate: dateRange?.end
        }
      });
      setData(prevData => ({ ...prevData, overview: response.data }));
    } catch (error) {
      console.error("Error fetching stat cards data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchData(dateRange);
    }
  }, [organizationId]);

  const filterDataByDateRange = (data, start, end) => {
    if (!start || !end || !data) return data;
    return data.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= new Date(start) && itemDate <= new Date(end);
    });
  };

  const filteredCallVolumeData = useMemo(
    () =>
      filterDataByDateRange(data.callVolume, dateRange?.start, dateRange?.end),
    [data.callVolume, dateRange]
  );

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="flex-1 relative z-10 overflow-auto">
      <div className="max-w-7xl mx-auto py-4 px-4 lg:px-8">
        <div className="flex justify-end">
          <DateRangeSelector onDateRangeChange={setDateRange} />
        </div>
      </div>
      <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
        <motion.div
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8 mt-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <StatCard 
            title="Total Call Volume" 
            icon={Zap} 
            value={data.overview?.totalCallVolume ?? 'N/A'} 
            color="#6366F1"
          />
          <StatCard 
            title="Call Success Rate" 
            icon={ShoppingBag} 
            value={data.overview?.callSuccessRate ? `${parseFloat(data.overview.callSuccessRate).toFixed(2)}%` : 'N/A'} 
            color="#EC4899"
          />
          <StatCard 
            title="Avg. Call Duration" 
            icon={BarChart2} 
            value={data.overview?.avgCallDuration ? `${data.overview.avgCallDuration}s` : 'N/A'} 
            color="#10B981"
          />
        </motion.div>

        {/* New Modern Metrics + Charts Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Left: Metric Cards */}
          <div className="md:col-span-1">
            <MetricCards metrics={[
              { icon: <span role="img" aria-label="timer">⏱️</span>, label: 'Average Response Time', value: `${(Number(avgResponseTime) || 0).toFixed(1)}s`, change: -12.3 },
              { icon: <span role="img" aria-label="handoff">🧑‍🤝‍🧑</span>, label: 'Human Handoff Rate', value: `${(Number(humanHandoffRate) || 0).toFixed(1)}%`, change: -8.9 },
              { icon: <span role="img" aria-label="chat">💬</span>, label: 'Multi-turn Success Rate', value: `${(Number(multiTurnSuccessRate) || 0).toFixed(1)}%`, change: 4.2 },
              { icon: <span role="img" aria-label="check">✅</span>, label: 'First Contact Resolution', value: `${(Number(firstContactResolution.percent) || 0).toFixed(1)}%`, change: Number(firstContactResolution.change) || 0 },
              { icon: <span role="img" aria-label="target">🎯</span>, label: 'Intent Recognition Accuracy', value: `${(Number(intentRecognitionAccuracy.accuracy) || 0).toFixed(1)}%`, change: Number(intentRecognitionAccuracy.change) || 0 },
              { icon: <span role="img" aria-label="context">🔊</span>, label: 'Context Retention Score', value: '89.7%', change: 1.8 },
              { icon: <span role="img" aria-label="escalation">⚠️</span>, label: 'Escalation Prevention', value: `${(Number(escalationPrevention.prevention) || 0).toFixed(1)}%`, change: Number(escalationPrevention.change) || 0 },
              { icon: <span role="img" aria-label="performance">📊</span>, label: 'Peak Hour Performance', value: `${(Number(peakHourPerformance.performance) || 0).toFixed(1)}%`, change: Number(peakHourPerformance.change) || 0 },
            ]} />
          </div>
          {/* Right: Charts and Intents Table */}
          <div className="md:col-span-2 flex flex-col gap-8">
            <DepartmentPieChart data={departmentCallData} title="Department-wise Call Distribution" />
            <HourlyPerformanceChart data={hourlyData} />
            <WeeklyTrendsChart data={weeklyData} />
            <DepartmentPieChart data={sentimentData} title="Customer Sentiment Analysis" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 mb-8">
          <PerformanceMetricsChart data={performanceMetrics} />
          <IntentsTable data={intentTableData} />
        </div>
      </main>
      {/*  <UserInteractions /> */}
      <div>
        {/*  <Reports /> */}
      </div>
    </div>
  );
};

export default DashboardOverview; 